import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from "@tiptap/react";
import { CODE_LANGUAGES } from "./code-languages";
import { useIsEditable } from "./use-is-editable";

/**
 * A code block with a language picker.
 *
 * The picker only exists while editing — at rest the block is prose, and a
 * disabled `<select>` floating over it would read as chrome the reader can act
 * on. It also stays hidden until hover or focus so it does not compete with the
 * code itself.
 */
export function CodeBlockNodeView({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const language = (node.attrs.language as string | null) ?? "plaintext";
  /* Not `editor.isEditable` read inline: an inline field flips that on every
   * click in and every commit out, and a plain read never re-renders. */
  const editable = useIsEditable(editor);

  return (
    <NodeViewWrapper className="rte-code-block group/code relative">
      {editable && (
        <select
          /* ProseMirror owns everything inside the node view by default; this
           * control is chrome, not content, so hand it back to the browser. */
          contentEditable={false}
          value={language}
          onChange={(e) => updateAttributes({ language: e.target.value })}
          /* Without this the editor's selection handling swallows the click
           * before the native picker opens. */
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute right-1.5 top-1.5 h-5 rounded-xs bg-sunken-strong px-1 text-xs text-text-tertiary opacity-0 transition-opacity duration-100 ease-out-fast focus:opacity-100 group-hover/code:opacity-100 focus-visible:outline-none focus-fill"
          /* Opts out of opero-app's unlayered form-fill rule, same reason as
           * `InlineEdit`'s BARE_INPUT_PROPS. */
          data-bare-input=""
          aria-label="Code language"
        >
          {CODE_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      )}
      <pre>
        <NodeViewContent as="code" />
      </pre>
    </NodeViewWrapper>
  );
}
